import React, { useEffect, useRef, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Play, CheckCircle2, AlertCircle } from "lucide-react";
import { formatDateTime } from "@/lib/formatDate";

const RUNNING = ["starting", "running"];

export function StorageMigrationPanel({ initial, legacyAvailable, onDone }) {
  const [state, setState] = useState(initial || { status: "idle" });
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const poll = async () => {
    try {
      const r = await apiClient.get("/admin/storage-config/migrate");
      setState(r.data);
      if (!RUNNING.includes(r.data.status)) {
        clearInterval(timer.current);
        timer.current = null;
        onDone?.();
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (RUNNING.includes(state.status) && !timer.current) timer.current = setInterval(poll, 2000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [state.status]);

  const start = async () => {
    setStarting(true);
    try {
      await apiClient.post("/admin/storage-config/migrate");
      toast.success("Migration gestartet");
      setState({ status: "starting", total: 0, done: 0, ok: 0, failed: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration konnte nicht gestartet werden");
    } finally {
      setStarting(false);
    }
  };

  const running = RUNNING.includes(state.status);
  const pct = state.total ? Math.round((state.done / state.total) * 100) : 0;

  return (
    <div data-testid="storage-migration-panel">
      {!legacyAvailable ? (
        <p className="text-xs text-slate-500" data-testid="storage-migration-unavailable">
          Alte Dateiablage nicht mehr verbunden – Migration abgeschlossen oder nicht nötig.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" onClick={start} disabled={starting || running} data-testid="storage-migrate-btn">
            {running || starting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
            {running ? "Migration läuft …" : "Migration starten"}
          </Button>
          <span className="text-xs text-slate-500">Kopiert alle Dateien aus der bisherigen Ablage in deinen Bucket. Mehrfach ausführbar.</span>
        </div>
      )}

      {state.status && state.status !== "idle" && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="storage-migration-status">
          {running && (
            <div className="h-2 w-full rounded bg-slate-200 overflow-hidden mb-2">
              <div className="h-2 bg-emerald-500 transition-[width]" style={{ width: `${pct}%` }} />
            </div>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span className="flex items-center gap-1 font-medium">
              {state.status === "done" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
              {(state.status === "done_with_errors" || state.status === "error") && <AlertCircle className="h-3.5 w-3.5 text-rose-600" />}
              {{ starting: "Startet …", running: `Läuft – ${pct} %`, done: "Abgeschlossen", done_with_errors: "Abgeschlossen mit Fehlern", error: "Abgebrochen" }[state.status] || state.status}
            </span>
            {state.total !== undefined && <span>{state.done}/{state.total} kopiert · ok {state.ok} · Fehler {state.failed}</span>}
            {state.source_count !== undefined && <span>Quelle {state.source_count} · Ziel {state.target_count}</span>}
            {state.finished_at && <span>beendet {formatDateTime(state.finished_at)}</span>}
          </div>
          {state.error && <div className="mt-1 text-rose-700">{state.error}</div>}
          {state.errors?.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-rose-700">{state.errors.map((e) => <li key={e}>{e}</li>)}</ul>
          )}
        </div>
      )}
    </div>
  );
}
